import type { BasePermission, ConsoleEventListenerProps, EmptyObject } from '@snapchat/logging';
import { ConsoleEventListener, LoggingEventType } from '@snapchat/logging';

type Props<LoggingContext, OtherEvents> = Omit<
  ConsoleEventListenerProps<LoggingContext, OtherEvents>,
  'messageTransform'
>;

export class ConsoleClientEventListener<
  LoggingContext extends EmptyObject = EmptyObject,
  OtherEvents extends EmptyObject = EmptyObject,
  Permission extends BasePermission = BasePermission
> extends ConsoleEventListener<LoggingContext, OtherEvents, Permission> {
  private readonly clientProps: Props<LoggingContext, OtherEvents>;

  constructor(props: Props<LoggingContext, OtherEvents> = {}) {
    super({
      ...props,
      messageTransform: ConsoleClientEventListener.messageTransform,
    });
    this.clientProps = props;
  }

  /** Adds colors See https://www.w3docs.com/snippets/javascript/colors-in-javascript-console.html */
  protected static messageTransform = (type: LoggingEventType, message: unknown[]): unknown[] => {
    if (message.length < 1) return message;

    const firstMessage = message.shift();
    const firstMessageWithColor = `%c${firstMessage}`;

    switch (type) {
      case LoggingEventType.ERROR:
        return [firstMessageWithColor, 'color: bloodOrange;', ...message];
      case LoggingEventType.WARNING:
        return [firstMessageWithColor, 'color: yellow;', ...message];
      case LoggingEventType.TIMING:
        return [firstMessageWithColor, 'color: dodgerblue;', ...message];
      case LoggingEventType.VALUE:
        return [firstMessageWithColor, 'color: dodgerblue;', ...message];
      case LoggingEventType.DEBUG:
        return [firstMessageWithColor, 'color: gray;', ...message];
      case LoggingEventType.CUSTOM:
        return [firstMessageWithColor, 'font-weight: bold;', ...message];
      default:
        return [firstMessage, ...message];
    }
  };
}
